import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Send, Loader2, CheckCircle2, MailWarning, Eye } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const cls = {
    initiated: 'bg-zinc-50 border-zinc-200 text-zinc-700',
    failed: 'bg-red-50 border-red-200 text-red-700',
    pending: 'bg-amber-50 border-amber-200 text-amber-700',
    cancelled: 'bg-zinc-50 border-zinc-200 text-zinc-500',
  }[status] || 'bg-zinc-50 border-zinc-200 text-zinc-700';
  return <span className={`inline-block px-2 py-0.5 text-xs font-mono rounded-md border ${cls}`}>{status}</span>;
};

const fmtDate = (s) => { try { return new Date(s).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return s; } };
const NONE = '__none__';
const PERIODS = [
  { v: '1', label: "Aujourd'hui (24h)" },
  { v: '3', label: '3 derniers jours' },
  { v: '7', label: '7 derniers jours' },
  { v: '14', label: '14 derniers jours' },
  { v: '30', label: '30 derniers jours' },
  { v: '90', label: '90 derniers jours' },
];

export function CartsPanel({ storeId, configured, templates, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);
  const [recentSending, setRecentSending] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [templateId, setTemplateId] = useState(NONE);
  const [periodDays, setPeriodDays] = useState('14');
  const [preview, setPreview] = useState({ open: false, loading: false, html: '', subject: '', to: '' });

  const load = () => {
    setLoading(true);
    const params = { days: Number(periodDays), min_age_minutes: 20, limit: 500 };
    if (storeId) params.store_id = storeId;
    api.get('/recovery', { params })
      .then((r) => { setItems(r.data.items || []); setSelected(new Set()); })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [storeId, periodDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendableIds = items.filter((i) => i.recovery_url).map((i) => i.id);
  const allSelected = sendableIds.length > 0 && sendableIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(sendableIds));
  const toggleOne = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const tplArg = templateId === NONE ? '' : templateId;

  const sendOne = async (row) => {
    if (!window.confirm(`Envoyer une relance à ${row.email} ?`)) return;
    setSendingId(row.id);
    try {
      await api.post(`/recovery/${row.id}/send`, null, { params: tplArg ? { template_id: tplArg } : {} });
      toast.success(`Relance envoyée à ${row.email}`);
      setItems((prev) => prev.map((i) => i.id === row.id
        ? { ...i, recovery_sent_at: new Date().toISOString(), recovery_sent_count: (i.recovery_sent_count || 0) + 1 } : i));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally { setSendingId(null); }
  };

  const sendBulk = async () => {
    if (selected.size === 0) return;
    setBulkSending(true);
    try {
      const { data } = await api.post('/recovery/send-bulk', { ids: [...selected], template_id: tplArg });
      toast.success(`${data.sent} envoyé(s) · ${data.skipped} ignoré(s) · ${data.failed} échec(s)`);
      if (data.errors?.length) toast.error(data.errors[0]);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi groupé");
    } finally { setBulkSending(false); }
  };

  const sendRecent = async (hours = 3) => {
    const cutoff = Date.now() - hours * 3600 * 1000;
    const ids = items
      .filter((i) => i.recovery_url && new Date(i.created_at).getTime() >= cutoff)
      .map((i) => i.id);
    if (ids.length === 0) { toast.info(`Aucun panier éligible sur les ${hours} dernières heures`); return; }
    if (!window.confirm(`Envoyer la relance « souci technique résolu » à ${ids.length} client(s) des ${hours} dernières heures ?`)) return;
    setRecentSending(true);
    try {
      const { data } = await api.post('/recovery/send-bulk', { ids, template_id: tplArg });
      toast.success(`${data.sent} envoyé(s) · ${data.skipped} ignoré(s) · ${data.failed} échec(s)`);
      if (data.errors?.length) toast.error(data.errors[0]);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally { setRecentSending(false); }
  };

  const openPreview = async (row) => {
    setPreview({ open: true, loading: true, html: '', subject: '', to: row.email });
    try {
      const { data } = await api.post('/recovery/preview', { tx_id: row.id, template_id: tplArg });
      setPreview({ open: true, loading: false, html: data.html, subject: data.subject, to: data.to });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aperçu indisponible");
      setPreview({ open: false, loading: false, html: '', subject: '', to: '' });
    }
  };

  return (
    <div data-testid="recovery-carts-panel">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">Période :</span>
          <Select value={periodDays} onValueChange={setPeriodDays}>
            <SelectTrigger className="h-9 w-44" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">Template :</span>
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger className="h-9 w-52" data-testid="bulk-template-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Template par défaut</SelectItem>
              {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? ' ★' : ''}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={sendBulk} disabled={selected.size === 0 || bulkSending || !configured}
                data-testid="bulk-send-button">
          {bulkSending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
          Envoyer ({selected.size})
        </Button>
        <Button onClick={() => sendRecent(3)} disabled={recentSending || !configured}
                variant="secondary" data-testid="bulk-send-recent-button"
                title="Relancer tous les clients ayant tenté de payer ces 3 dernières heures (message : souci technique résolu)">
          {recentSending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
          Relancer 3 dernières heures
        </Button>
        <span className="text-xs text-zinc-500 font-mono ml-auto" data-testid="recovery-count">{items.length} panier(s) · {sendableIds.length} éligible(s)</span>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="recovery-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Rafraîchir'}
        </Button>
      </div>

      {!configured && (
        <div className="mb-4 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="recovery-not-configured">
          <MailWarning className="h-4 w-4 mt-0.5 shrink-0" />
          <span>L'envoi d'emails n'est pas configuré (clé Resend manquante).</span>
        </div>
      )}

      <Card className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 text-left text-xs text-zinc-500 uppercase tracking-wider">
                <th className="px-4 py-3 w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-carts" /></th>
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-4 py-3 font-medium">Produit</th>
                <th className="px-4 py-3 font-medium">Montant</th>
                <th className="px-4 py-3 font-medium">Statut</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium text-right">Relance</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-400" data-testid="recovery-empty">
                  {loading ? 'Chargement…' : 'Aucun panier à relancer 🎉'}
                </td></tr>
              )}
              {items.map((row) => {
                const noLink = !row.recovery_url;
                return (
                  <tr key={row.id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`recovery-row-${row.id}`}>
                    <td className="px-4 py-3">
                      <Checkbox checked={selected.has(row.id)} disabled={noLink}
                                onCheckedChange={() => toggleOne(row.id)} data-testid={`select-cart-${row.id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-900">{row.name || '—'}</div>
                      <div className="text-xs text-zinc-500 font-mono">{row.email}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-700 max-w-[200px] truncate">{row.product_name || `${row.items_count} article(s)`}</td>
                    <td className="px-4 py-3 font-mono">{Number(row.amount).toFixed(2)} €</td>
                    <td className="px-4 py-3"><StatusBadge status={row.payment_status} /></td>
                    <td className="px-4 py-3 text-xs text-zinc-500">{fmtDate(row.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {row.recovery_sent_count > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 mr-2" title={fmtDate(row.recovery_sent_at)}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> ×{row.recovery_sent_count}
                        </span>
                      )}
                      <Button size="sm" variant="ghost" className="mr-1" disabled={noLink}
                              onClick={() => openPreview(row)} data-testid={`recovery-preview-${row.id}`}
                              title={noLink ? 'Lien de relance indisponible (ancien panier)' : "Aperçu de l'email"}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant={row.recovery_sent_count > 0 ? 'outline' : 'default'}
                              disabled={sendingId === row.id || !configured || noLink}
                              onClick={() => sendOne(row)} data-testid={`recovery-send-${row.id}`}
                              title={noLink ? 'Lien de relance indisponible (ancien panier)' : ''}>
                        {sendingId === row.id ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <><Send className="h-3.5 w-3.5 mr-1.5" /> {row.recovery_sent_count > 0 ? 'Renvoyer' : 'Envoyer'}</>}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={preview.open} onOpenChange={(o) => setPreview((p) => ({ ...p, open: o }))}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle>Aperçu de l'email</DialogTitle>
            <DialogDescription>
              {preview.to ? <>Destinataire : <span className="font-mono">{preview.to}</span></> : 'Email tel qu\'il sera envoyé.'}
            </DialogDescription>
          </DialogHeader>
          {preview.loading ? (
            <div className="py-16 text-center text-zinc-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : (
            <div>
              <div className="text-sm mb-2"><span className="text-zinc-400">Sujet : </span><span className="font-medium text-zinc-900">{preview.subject}</span></div>
              <iframe title="email-preview" srcDoc={preview.html} data-testid="email-preview-iframe"
                      className="w-full h-[60vh] rounded-lg border border-zinc-200 bg-white" sandbox="" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
